import React, { forwardRef, useCallback } from 'react';
import Proptypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import clsx from 'clsx';
import InputAdornment from '@material-ui/core/InputAdornment';
import removeNullProps from '../../lib/removeNullProps';
import { makeStyles } from '../../lib/styles';
import InfoAdornment from './InfoAdornment';

const useStyles = makeStyles(theme => {
  return {
    root: {
      '&:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) $notchedOutline': {
        borderColor: theme.palette.text.secondary,
      },
    },
    notchedOutline: {
      borderWidth: '1px !important',
      '&:hover': {
        borderColor: theme.palette.text.secondary,
      },
    },
    underline: {
      '&:after': {
        borderBottomWidth: 1,
      },
      '&:before': {
        borderBottomColor: theme.palette.common.muted,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
      },
      '&.Mui-disabled:before': {
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.text.disabled,
      },
      '&:hover:not(.Mui-disabled):before': {
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.text.secondary,
      },
    },
    errorSpace: {
      paddingBottom: theme.spacing(2),
    },
    startAdornmentRoot: {
      color: theme.palette.text.secondary,
    },
    endAdornmentRoot: {
      color: theme.palette.text.secondary,
    },
  };
});

const Input = forwardRef(function Input(props, ref) {
  const {
    children,
    className,
    dataAttrs,
    defaultValue,
    disabled,
    error,
    errorText,
    fullWidth,
    helpText,
    id,
    infoText,
    inputRef,
    label,
    maxLength,
    max,
    min,
    minLength,
    name,
    onBlur,
    onChange,
    pattern,
    required,
    select,
    selectProps,
    title,
    type,
    value,
    startAdornment,
    endAdornment,
    variant,
    ...restProps
  } = props;
  const handleOnChange = useCallback(event => onChange && onChange(event.target.value, event), [
    onChange,
  ]);
  const handleOnBlur = useCallback(event => onBlur && onBlur(event.target.value, event), [onBlur]);
  const {
    underline,
    errorSpace,
    startAdornmentRoot,
    endAdornmentRoot,
    notchedOutline,
    root,
  } = useStyles();

  const inputProps = {
    classes: removeNullProps({
      root,
      underline: variant === 'standard' ? underline : null,
      notchedOutline: variant === 'outlined' ? notchedOutline : null,
    }),
    inputProps: removeNullProps({
      max,
      maxLength,
      min,
      minLength,
      pattern,
      ...dataAttrs,
    }),
    id,
    name,
    title,
  };

  if (startAdornment) {
    inputProps.startAdornment = (
      <InputAdornment position="start" className={startAdornmentRoot}>
        {startAdornment}
      </InputAdornment>
    );
  }

  if (endAdornment) {
    inputProps.endAdornment = (
      <InputAdornment position="end" className={endAdornmentRoot}>
        {endAdornment}
      </InputAdornment>
    );
  }

  if (!select && infoText) {
    inputProps.endAdornment = <InfoAdornment infoText={infoText} />;
  }

  const inputLabelProps = { htmlFor: id };
  if (type === 'date') {
    inputLabelProps.shrink = true;
  }

  const optionalProps = removeNullProps({
    defaultValue,
    value,
    label,
  });

  return (
    <TextField
      {...restProps}
      {...optionalProps}
      InputLabelProps={inputLabelProps}
      InputProps={inputProps}
      SelectProps={{ id, name, ...selectProps }}
      className={clsx([className, errorText && !error && errorSpace])}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      helperText={(error && errorText) || helpText}
      inputRef={inputRef}
      margin="dense"
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      ref={ref}
      required={required}
      select={select}
      type={type}
      variant={variant}
    >
      {children}
    </TextField>
  );
});

Input.propTypes = {
  id: Proptypes.string.isRequired,
  name: Proptypes.string.isRequired,
  onChange: Proptypes.func,
  children: Proptypes.node,
  className: Proptypes.string,
  dataAttrs: Proptypes.object, // eslint-disable-line
  disabled: Proptypes.bool,
  defaultValue: Proptypes.string,
  error: Proptypes.bool,
  errorText: Proptypes.string,
  fullWidth: Proptypes.bool,
  helpText: Proptypes.string,
  infoText: Proptypes.string,
  inputRef: Proptypes.oneOfType([Proptypes.func, Proptypes.object]),
  label: Proptypes.string,
  max: Proptypes.string,
  maxLength: Proptypes.string,
  min: Proptypes.string,
  minLength: Proptypes.string,
  onBlur: Proptypes.func,
  pattern: Proptypes.string,
  required: Proptypes.bool,
  select: Proptypes.bool,
  selectProps: Proptypes.shape({
    classes: Proptypes.object, // eslint-disable-line react/forbid-prop-types
    MenuProps: Proptypes.object, // eslint-disable-line react/forbid-prop-types
  }),
  title: Proptypes.string,
  type: Proptypes.oneOf(['text', 'number', 'email', 'date', 'password', 'tel']),
  value: Proptypes.oneOfType([Proptypes.string, Proptypes.object, Proptypes.number]), // NOTE: upstream uses any, this is safe to change too
  startAdornment: Proptypes.element,
  endAdornment: Proptypes.element,
  variant: Proptypes.oneOf(['standard', 'outlined']),
};

Input.defaultProps = {
  children: null,
  className: '',
  dataAttrs: {},
  defaultValue: null,
  disabled: false,
  error: false,
  errorText: '',
  fullWidth: true,
  helpText: '',
  infoText: '',
  inputRef: null,
  label: null,
  max: null,
  maxLength: null,
  min: null,
  minLength: null,
  onBlur: null,
  onChange: null,
  pattern: null,
  required: false,
  select: false,
  selectProps: null,
  title: '',
  type: 'text',
  value: null,
  startAdornment: null,
  endAdornment: null,
  variant: 'standard',
};

Input.displayName = 'Input';

export default Input;
